import { Link } from "react-router-dom";
import { useLanguage } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import logo from '@/assets/igniteaitool-logo.jpg';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ArrowRight, Download } from 'lucide-react';

const ChromeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="24" cy="24" r="22" fill="none" stroke="currentColor" strokeWidth="2"/>
    <path d="M24 14C29.523 14 34 18.477 34 24H26.5L17.17 7.84A21.8 21.8 0 0124 6z" fill="#EA4335" opacity="0.9"/>
    <path d="M34 24c0 5.523-4.477 10-10 10l-5.5-9.5L26.83 7.84A21.8 21.8 0 0142 24z" fill="#FBBC05" opacity="0.9"/>
    <path d="M14 24c0-3.59 1.89-6.74 4.73-8.5L24 24l-5.5 9.5A21.8 21.8 0 016 24z" fill="#4285F4" opacity="0.9"/>
    <path d="M24 34c-5.523 0-10-4.477-10-10h7.5l8.33 16.16A21.8 21.8 0 0124 42z" fill="#34A853" opacity="0.9"/>
    <circle cx="24" cy="24" r="6" fill="white"/>
    <circle cx="24" cy="24" r="4" fill="#4285F4"/>
  </svg>
);

const Footer = () => {
  const { t } = useLanguage();
  const { user } = useAuth();

  return (
    <footer className="border-t bg-card/50 backdrop-blur-sm mt-6">
      <div className="container mx-auto px-4 py-6 sm:py-8 text-center text-sm text-muted-foreground">
        {/* Final push CTA for guests */}
        {!user && (
          <div className="mb-5 sm:mb-6">
            <Link to="/auth">
              <Button variant="outline" size="lg" className="text-sm sm:text-base px-6 sm:px-8 border-primary/30 hover:bg-primary hover:text-primary-foreground hover:scale-105 transition-all">
                {t('footer.startTranslating')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        )}

        {/* Chrome Extension section */}
        <div className="mb-5 sm:mb-6 p-4 sm:p-5 rounded-xl border border-border/50 bg-background/50 max-w-md mx-auto">
          <div className="flex items-center justify-center gap-2 mb-2">
            <ChromeIcon />
            <h3 className="text-sm font-semibold text-foreground">{t('footer.chromeExtTitle')}</h3>
          </div>
          <p className="text-xs text-muted-foreground mb-3">{t('footer.chromeExtDesc')}</p>
          <a
            href="https://drive.google.com/drive/folders/1YDe798n1uIyaNXV2ss7RMIM12ZcvaqeF?usp=sharing"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-2 text-xs sm:text-sm font-medium text-primary transition-all duration-300 hover:scale-105 hover:bg-primary hover:text-primary-foreground hover:shadow-md"
          >
            <Download className="h-3.5 w-3.5" />
            <span>{t('footer.addToChrome')}</span>
            <span className="rounded-full bg-primary/20 px-1.5 py-px text-[10px] font-bold uppercase leading-tight">
              {t('footer.free')}
            </span>
          </a>
          <div className="mt-2 flex flex-col items-center gap-1.5">
            <Link
              to="/extension-installation-guide"
              className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors"
            >
              <span>📘</span>
              <span>{t('footer.chromeExtManual')}</span>
            </Link>
          </div>
        </div>

        <div className="flex items-center justify-center gap-2">
          <img src={logo} alt="IgniteAiTool logo" className="h-6 w-6 rounded-full" />
          <span className="text-xs sm:text-sm">{t('footer.copyright')}</span>
        </div>
        <div className="mt-2 flex flex-wrap items-center justify-center gap-3 sm:gap-4">
          <Link to="/contact" className="text-primary hover:underline text-xs sm:text-sm">{t('footer.contactSupport')}</Link>
          <span className="text-muted-foreground/50">·</span>
          <Link to="/privacy-policy" className="text-primary hover:underline text-xs sm:text-sm">{t('footer.privacyPolicy')}</Link>
          <span className="text-muted-foreground/50">·</span>
          <Link to="/terms-of-service" className="text-primary hover:underline text-xs sm:text-sm">{t('footer.termsOfService')}</Link>
          <span className="text-muted-foreground/50">·</span>
          <Link to="/admin" className="text-muted-foreground hover:text-primary text-xs">{t('footer.admin')}</Link>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
